import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import * as analyticsApi from "../api/analyticsApi";

const STORAGE_KEY_PREFIX = "pinnedBoards_";

function getStoredPinnedIds(teamId) {
  if (!teamId || typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + teamId);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function setStoredPinnedIds(teamId, ids) {
  if (!teamId || typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + teamId, JSON.stringify(ids));
  } catch (_) {}
}

const defaultValue = {
  pinnedBoardIds: [],
  togglePin: () => {},
  isPinned: () => false,
};

const PinnedBoardsContext = createContext(defaultValue);

export function PinnedBoardsProvider({ teamId, children }) {
  const [pinnedBoardIds, setPinnedBoardIds] = useState(() => getStoredPinnedIds(teamId));

  // Load pinned board IDs from API when teamId is set (sync across devices)
  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await analyticsApi.fetchPinnedBoards();
        const data = res?.data;
        const ids = data?.boardIds;
        if (!cancelled && Array.isArray(ids)) {
          const strIds = ids.map((id) => String(id));
          setPinnedBoardIds(strIds);
          setStoredPinnedIds(teamId, strIds);
        }
      } catch (_) {
        // Fallback: keep localStorage value (already in state)
      }
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  const togglePin = useCallback(
    (boardId) => {
      if (!teamId || boardId == null) return;
      const id = String(boardId);
      setPinnedBoardIds((prev) => {
        const next = prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id];
        setStoredPinnedIds(teamId, next);
        // Persist to server (fire-and-forget; localStorage already updated)
        analyticsApi.updatePinnedBoards(next.map((s) => (s === String(parseInt(s, 10)) ? parseInt(s, 10) : s))).catch(() => {});
        return next;
      });
    },
    [teamId]
  );

  const isPinned = useCallback(
    (boardId) => pinnedBoardIds.includes(String(boardId)),
    [pinnedBoardIds]
  );

  const value = useMemo(
    () => ({ pinnedBoardIds, togglePin, isPinned }),
    [pinnedBoardIds, togglePin, isPinned]
  );

  return (
    <PinnedBoardsContext.Provider value={value}>
      {children}
    </PinnedBoardsContext.Provider>
  );
}

export function usePinnedBoards() {
  return useContext(PinnedBoardsContext);
}

export default PinnedBoardsContext;
